// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Results Panel
// Shows KPIs, charts, summary table, cash flow, sensitivity analysis
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { Download, Printer, ChevronDown, ChevronUp } from 'lucide-react';

import { KPICards } from './KPICards';
import { CostBreakdownCharts } from '@/components/charts/CostBreakdownCharts';
import { CashFlowTable } from './CashFlowTable';
import { SensitivityTable } from './CashFlowTable';
import { Card, CardHeader, CardBody, CardFooter } from '@/components/ui/index';
import { Button } from '@/components/ui/Button';
import { fmt, fmtShort, fmtPercent, downloadCSV } from '@/lib/utils';
import type { LCCAResults, LCCAParameters } from '@lcca-nexus/shared';

interface ResultsPanelProps {
  results: LCCAResults;
  params: LCCAParameters;
  projectName?: string;
}

export function ResultsPanel({ results, params, projectName = 'LCCA Analysis' }: ResultsPanelProps) {
  const [showAllRows, setShowAllRows] = useState(false);

  const handleExportCSV = () => {
    const rows: string[][] = [
      ['LCCA Report', projectName],
      ['Generated', new Date().toLocaleString()],
      ['Analysis Period', `${params.analysisPeriod} years`],
      ['Discount Rate', `${params.discountRate}%`],
      [],
      ['=== COST SUMMARY ==='],
      ['Category', 'Undiscounted ($)', 'Present Worth ($)', '% of LCC'],
      ['Initial Cost', String(params.initialCost), String(results.initialCostPW.toFixed(2)), fmtPercent(results.initialCostPW / results.totalLCC * 100)],
      ['O&M Costs', String(results.omUndiscounted.toFixed(2)), String(results.omPW.toFixed(2)), fmtPercent(results.omPW / results.totalLCC * 100)],
      ['Energy Costs', String(results.energyUndiscounted.toFixed(2)), String(results.energyPW.toFixed(2)), fmtPercent(results.energyPW / results.totalLCC * 100)],
      ['Replacement Costs', String(results.replacementUndiscounted.toFixed(2)), String(results.replacementPW.toFixed(2)), fmtPercent(results.replacementPW / results.totalLCC * 100)],
      ['Residual Value (−)', String(-params.residualValue), String(-results.residualPW.toFixed(2)), fmtPercent(-results.residualPW / results.totalLCC * 100)],
      ['TOTAL LCC', '', String(results.totalLCC.toFixed(2)), '100%'],
      [],
      ['=== YEAR-BY-YEAR CASH FLOWS ==='],
      ['Year', 'O&M ($)', 'Energy ($)', 'Replacement ($)', 'Total ($)', 'Discount Factor', 'Present Worth ($)', 'Cumulative PW ($)'],
      ...results.cashFlows.map(y => [
        String(y.year), y.omCost.toFixed(2), y.energyCost.toFixed(2),
        y.replacementCost.toFixed(2), y.totalCost.toFixed(2),
        y.discountFactor.toFixed(4), y.presentWorth.toFixed(2), y.cumulativePW.toFixed(2),
      ]),
    ];

    downloadCSV(`LCCA_${projectName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.csv`, rows);
  };

  // Summary table rows
  const summaryRows = [
    { label: 'Initial Capital Cost', undis: params.initialCost, pw: results.initialCostPW },
    { label: 'O&M Costs',            undis: results.omUndiscounted,          pw: results.omPW },
    { label: 'Energy Costs',         undis: results.energyUndiscounted,      pw: results.energyPW },
    { label: 'Replacement Costs',    undis: results.replacementUndiscounted, pw: results.replacementPW },
    { label: 'Residual Value (−)',   undis: -params.residualValue,           pw: -results.residualPW },
  ];

  const displayedYears = showAllRows
    ? results.cashFlows
    : results.cashFlows.slice(0, 10);

  return (
    <div className="flex flex-col gap-5">

      {/* KPIs */}
      <KPICards results={results} params={params} />

      {/* Charts */}
      <CostBreakdownCharts results={results} params={params} />

      {/* Cost Summary Table */}
      <Card className="animate-fade-up">
        <CardHeader>
          <div className="font-display font-bold text-navy-900 text-sm flex-1">📋 LCCA Cost Summary</div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" icon={<Printer size={13} />} onClick={() => window.print()}>
              Print
            </Button>
            <Button size="sm" variant="gold" icon={<Download size={13} />} onClick={handleExportCSV}>
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cost Category</th>
                <th>Undiscounted ($)</th>
                <th>Present Worth ($)</th>
                <th>% of LCC</th>
                <th style={{ width: '120px' }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map(row => {
                const pct = Math.abs(row.pw) / results.totalLCC * 100;
                const isNeg = row.pw < 0;
                return (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{fmt(row.undis)}</td>
                    <td className={isNeg ? 'text-teal-700' : ''}>{fmt(row.pw)}</td>
                    <td className={isNeg ? 'text-teal-700' : ''}>{fmtPercent(row.pw / results.totalLCC * 100)}</td>
                    <td>
                      <div className="w-full bg-parchment-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${isNeg ? 'bg-teal-500' : 'bg-navy-500'}`}
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td>TOTAL LIFE-CYCLE COST (NPV)</td>
                <td>{fmt(summaryRows.reduce((a, r) => a + r.undis, 0))}</td>
                <td>{fmt(results.totalLCC)}</td>
                <td>100%</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Cash Flow Table */}
      <CashFlowTable
        cashFlows={results.cashFlows}
        showAll={showAllRows}
        onToggleShowAll={() => setShowAllRows(v => !v)}
      />

      {/* Sensitivity Analysis */}
      <SensitivityTable sensitivity={results.sensitivity} baseLCC={results.totalLCC} />

      {/* Formula Reference */}
      <Card className="animate-fade-up bg-navy-900 text-white border-navy-800">
        <CardBody>
          <h3 className="font-display font-bold text-amber-400 text-sm mb-4 flex items-center gap-2">
            📐 LCCA Formulae Reference
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { name: 'Discount Factor',       eq: 'DF(t) = (1 + d)⁻ᵗ' },
              { name: 'Single Present Worth',  eq: 'SPW = F × (1 + d)⁻ᵗ' },
              { name: 'Uniform Present Worth', eq: 'UPW = [1−(1+d)⁻ᴺ] ÷ d' },
              { name: 'Escalating PW',         eq: 'Σ Cₜ × (1+e)ᵗ × (1+d)⁻ᵗ' },
              { name: 'Capital Recovery',      eq: 'CRF = d ÷ [1−(1+d)⁻ᴺ]' },
              { name: 'Total LCC',             eq: 'LCC = IC + PW(OM) + PW(E) + PW(R) − PW(RV)' },
            ].map(f => (
              <div key={f.name} className="bg-navy-800 rounded-lg p-3 border border-navy-700">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-navy-400 mb-1">{f.name}</div>
                <div className="font-mono text-amber-300 text-xs leading-relaxed">{f.eq}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-navy-500 mt-4">
            References: ISO 15686-5:2017 · ASTM E917 · NIST Handbook 135
          </p>
        </CardBody>
      </Card>

    </div>
  );
}
